import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Autocomplete, Box, Button, FormControlLabel, MenuItem, Pagination, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { useGetSyncResultsQuery } from "state/rtkQuery/sync/syncQueries";

import {
  dataDays,
  DEFAULT_HISTORY_FILTERS,
  formatDuration,
  hasActiveFilters,
  HISTORY_PAGE_SIZES,
  HISTORY_SORTS,
  HISTORY_STATUSES,
  LIVE_REFRESH_MS,
  parseHistoryParams,
  recordAsTask,
  toApiParams,
  writeHistoryParams,
} from "./historyFilters";
import { formatDateTime, normalizeApiError, timeAgo } from "./syncFormat";
import { taskLabel } from "./syncNames";
import { CallerCell, headCellSx, HistoryStatusPill, OutcomePill, Panel, QueueName, TaskName } from "./SyncParts";
import { taskNameFilter, useSyncTaskNames } from "./useSyncTaskNames";

// Run history from the retained task records: it survives reloads and includes runs started by other people. Filters,
// sort and page live in the address, so a filtered view can be reloaded or shared.
const HistoryTab = ({ onOpenRun }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => parseHistoryParams(searchParams), [searchParams]);
  // System tasks (recovery.*, beat.*) are left out unless asked for; naming a task yourself always works.
  const { userNames, allNames, ready, hasSystemRuns } = useSyncTaskNames();
  const taskOptions = filters.includeSystem ? allNames : userNames;

  // A reversed date range is rejected by the service (400), so it isn't sent.
  const rangeProblem = filters.from && filters.to && filters.to < filters.from ? "“Submitted to” is before “Submitted from”." : "";
  const waitingForNames = !filters.includeSystem && !filters.tasks.length && !ready;
  const apiParams = { ...toApiParams(filters), task_name: taskNameFilter({ chosen: filters.tasks, includeSystem: filters.includeSystem, userNames }) };
  // The list refreshes itself every 10 seconds, whether or not a run is going, so runs other people start show up too. It
  // pauses while this window is not in focus.
  const { data, isFetching, isError, error, refetch } = useGetSyncResultsQuery(apiParams, {
    skip: Boolean(rangeProblem) || waitingForNames,
    pollingInterval: LIVE_REFRESH_MS,
    skipPollingIfUnfocused: true,
  });
  const results = data?.results || [];
  const total = data?.total_count ?? 0;
  const pages = Math.max(1, Math.ceil(total / filters.size));
  const failure = isError ? normalizeApiError(error) : null;

  const update = (patch, { resetPage = true } = {}) =>
    setSearchParams(writeHistoryParams(searchParams, { ...filters, ...patch, page: resetPage ? 1 : patch.page ?? filters.page }), { replace: true });
  const clear = () => update({ ...DEFAULT_HISTORY_FILTERS, size: filters.size, sortBy: filters.sortBy, sortOrder: filters.sortOrder });

  return (
    <Panel
      title="Run history"
      subtitle="every retained run, including other people's"
      actions={
        <Button size="small" variant="outlined" onClick={refetch} sx={{ textTransform: "none" }}>↻ Refresh</Button>
      }
    >
      <Box sx={{ p: "14px 20px", display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start", borderBottom: "1px solid #f1f5f9" }} data-testid="history-filters">
        <Autocomplete
          multiple
          size="small"
          options={HISTORY_STATUSES}
          value={filters.statuses}
          onChange={(_, value) => update({ statuses: value })}
          getOptionLabel={(option) => option.charAt(0).toUpperCase() + option.slice(1)}
          sx={{ minWidth: 220 }}
          renderInput={(params) => <TextField {...params} label="Status" placeholder={filters.statuses.length ? "" : "Any"} />}
        />
        <Autocomplete
          multiple
          size="small"
          options={taskOptions}
          value={filters.tasks}
          onChange={(_, value) => update({ tasks: value })}
          getOptionLabel={(option) => taskLabel(option)}
          // Type either the plain name or the technical one.
          filterOptions={(options, { inputValue }) => options.filter((option) => `${taskLabel(option)} ${option}`.toLowerCase().includes(inputValue.trim().toLowerCase()))}
          renderOption={(props, option) => (
            <li {...props} key={option}>
              <TaskName name={option} />
            </li>
          )}
          sx={{ minWidth: 300 }}
          renderInput={(params) => <TextField {...params} label="Task" placeholder={filters.tasks.length ? "" : "Any"} />}
        />
        {hasSystemRuns && (
          <FormControlLabel
            control={<Switch size="small" color="success" checked={filters.includeSystem} onChange={(event) => update({ includeSystem: event.target.checked })} />}
            label={<span style={{ fontSize: 13 }}>Include system tasks</span>}
            sx={{ mt: 0.25, mr: 1 }}
            title="Housekeeping that runs all the time, such as recovery.stale and the scheduler refresh. Hidden by default."
          />
        )}
        <TextField size="small" label="Owner ID" value={filters.owner} onChange={(event) => update({ owner: event.target.value })} sx={{ width: 190 }} />
        <TextField size="small" label="Queue" value={filters.queue} onChange={(event) => update({ queue: event.target.value })} sx={{ width: 140 }} />
        <TextField size="small" type="date" label="Submitted from" InputLabelProps={{ shrink: true }} value={filters.from} onChange={(event) => update({ from: event.target.value })} />
        <TextField size="small" type="date" label="Submitted to" InputLabelProps={{ shrink: true }} value={filters.to} onChange={(event) => update({ to: event.target.value })} />
        <TextField select size="small" label="Sort by" value={filters.sortBy} onChange={(event) => update({ sortBy: event.target.value })} sx={{ width: 140 }}>
          {HISTORY_SORTS.map((sort) => <MenuItem key={sort.value} value={sort.value}>{sort.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Order" value={filters.sortOrder} onChange={(event) => update({ sortOrder: event.target.value })} sx={{ width: 120 }}>
          <MenuItem value="desc">Newest first</MenuItem>
          <MenuItem value="asc">Oldest first</MenuItem>
        </TextField>
        {hasActiveFilters(filters) && <Button size="small" onClick={clear} sx={{ textTransform: "none", mt: 0.5 }}>Clear filters</Button>}
      </Box>

      {rangeProblem ? (
        <Alert severity="warning" sx={{ m: 2 }} data-testid="history-range-problem">{rangeProblem}</Alert>
      ) : isError ? (
        <Alert severity="error" sx={{ m: 2 }} action={<Button color="inherit" size="small" onClick={refetch}>Retry</Button>} data-testid="history-error">
          {failure.status === 403
            ? "You don't have permission to read run history."
            : failure.status === 404
              ? "Run history isn't available on this environment yet. The backend change may not be deployed."
              : `Couldn't load the run history. ${failure.message}`}
        </Alert>
      ) : (isFetching && !data) || waitingForNames ? (
        <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>Loading run history…</Typography>
      ) : results.length === 0 ? (
        <Typography sx={{ p: 4, textAlign: "center", color: "#94a3b8" }} data-testid="history-empty">
          {hasActiveFilters(filters) ? "No runs match these filters." : "No runs are retained yet. Older runs are not backfilled."}
        </Typography>
      ) : (
        <>
          <Table size="small" sx={{ opacity: isFetching ? 0.6 : 1 }}>
            <TableHead>
              <TableRow>
                {["Task", "Data for", "Status", "Outcome", "Triggered by", "Submitted", "Duration", "Queue"].map((label) => <TableCell key={label} sx={headCellSx}>{label}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((record) => (
                <TableRow key={record.task_id} hover sx={{ cursor: "pointer" }} onClick={() => onOpenRun(record.task_id)} data-testid="history-row">
                  <TableCell><TaskName name={record.task_name} /></TableCell>
                  <TableCell sx={{ fontSize: 12.5, whiteSpace: "nowrap" }} data-testid="history-data-days">{dataDays(record) || <span style={{ color: "#cbd5e1" }}>—</span>}</TableCell>
                  <TableCell><HistoryStatusPill status={record.status} /></TableCell>
                  <TableCell><OutcomePill task={recordAsTask(record)} /></TableCell>
                  <TableCell><CallerCell source={record} /></TableCell>
                  <TableCell sx={{ fontSize: 12.5 }} title={formatDateTime(record.submitted_at)}>{timeAgo(record.submitted_at)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}>{formatDuration(record.duration_seconds)}</TableCell>
                  <TableCell sx={{ fontSize: 12.5 }}><QueueName queue={record.queue} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box sx={{ p: "12px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, borderTop: "1px solid #f1f5f9" }}>
            <Typography sx={{ color: "#64748b", fontSize: 12.5 }} data-testid="history-count">
              {(filters.page - 1) * filters.size + 1}–{(filters.page - 1) * filters.size + results.length} of {total.toLocaleString("en-US")} runs
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField select size="small" label="Rows" value={filters.size} onChange={(event) => update({ size: Number(event.target.value) })} sx={{ width: 90 }}>
                {HISTORY_PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
              </TextField>
              <Pagination count={pages} page={Math.min(filters.page, pages)} onChange={(_, page) => update({ page }, { resetPage: false })} shape="rounded" size="small" />
            </Box>
          </Box>
        </>
      )}
    </Panel>
  );
};

export default HistoryTab;
